import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  Divider,
  Link as ChakraLink,
  useColorModeValue,
  Flex,
  Badge,
} from '@chakra-ui/react'
import Link from 'next/link'
import Balancer from 'react-wrap-balancer'

const Patents = () => {
  return (
    <Box w="full">
      <Container maxW="4xl" py={16}>
        <Flex
          textAlign="center"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          mb="12"
        >
          <Heading fontSize="4xl" mb="4">
            <Balancer>Patent Information</Balancer>
          </Heading>
          <Text color={useColorModeValue('slate.600', 'slate.400')} fontSize="lg">
            <Balancer>
              This page identifies products protected by Foolproof Labs patents in compliance with
              35 U.S.C. § 287(a).
            </Balancer>
          </Text>
        </Flex>

        <Divider mb="8" />

        <Stack spacing={8}>
          {/* Product 1: Foolproof Token (FPT) */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Link href="/" passHref legacyBehavior>
                  <ChakraLink _hover={{ textDecoration: 'underline' }}>
                    <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                      Foolproof Token (FPT)
                    </Heading>
                  </ChakraLink>
                </Link>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    Foolproof Token offers unparalleled security and control over digital assets by creating a 1:1 linked token to prove ownership without risking ownership.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Product 2: Foolproof Token for Cross-Chain Asset Management and Moderation */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Link href="/cross-chain" passHref legacyBehavior>
                  <ChakraLink _hover={{ textDecoration: 'underline' }}>
                    <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                      Foolproof Token for Cross-Chain Asset Management and Moderation
                    </Heading>
                  </ChakraLink>
                </Link>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    Cryptographically-bound surrogate tokens that enable low-friction, low-cost proof of ownership and game participation across blockchain networks without requiring asset migration or cross-chain bridges. Facilitates secure token-gating, access control, and community moderation for gaming ecosystems and decentralized applications by allowing users to prove ownership of assets on one chain while participating in activities on another, eliminating the security risks and costs associated with traditional cross-chain asset transfers.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Product: Foolproof Token for Tokenized Real-World Assets */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Link href="/tokenized-assets" passHref legacyBehavior>
                  <ChakraLink _hover={{ textDecoration: 'underline' }}>
                    <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                      Foolproof Token for Tokenized Real-World Assets
                    </Heading>
                  </ChakraLink>
                </Link>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    Cryptographically-bound surrogate tokens that secure tokenized representations of real-world assets — including treasuries, equities, and yield-bearing instruments — by providing immutable proof of ownership and enabling controlled transferability without compromising the underlying asset. Enables multi-chain verification of tokenized RWA positions and compliant transfer workflows through on-chain surrogate token status.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Product 3: Foolproof Token for Concentrated Liquidity Positions */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                  Foolproof Token for Concentrated Liquidity Positions
                </Heading>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    Cryptographically-bound tokens that represent pool share ownership based on concentrated liquidity positions. Enables representation of liquidity stakes within specific price ranges as transferable tokens bound to the underlying NFT position.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Product 3: Foolproof Token for Liquid Staking Tokens */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                  Foolproof Token for Liquid Staking Tokens
                </Heading>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    Cryptographically-bound tokens that represent staking positions based on liquid staking tokens. Enables representation of validator staking positions as transferable tokens bound to the underlying liquid staking token.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Product 5: Foolproof Token for Immutable Control */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                  Foolproof Token for Immutable Control
                </Heading>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    Paired-key moderation system that enables organizations to maintain immutable control over digital asset permissions through a dual-contract architecture. The Center of Moderation deploys a principal token smart contract and assigns Foolproof Tokens to participants, enabling automated management through configurable logic for renewals, compliance verification, and access control. Organizations can manage users directly from the blockchain or through integrated dashboards, maintaining centralized oversight of decentralized communities while preserving the security and immutability of blockchain-based permissions.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Product 6: Foolproof Token for Centralized Moderation */}
          <Box
            p={8}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={useColorModeValue('slate.200', 'slate.700')}
            bg={useColorModeValue('white', 'slate.800')}
          >
            <Stack spacing={4}>
              <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Heading size="md" color={useColorModeValue('brand.800', 'brand.200')}>
                  Foolproof Token for Centralized Moderation
                </Heading>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  Active
                </Badge>
              </Flex>

              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Product Description
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('slate.700', 'slate.300')}>
                    On-chain proof of compliance system utilizing the Amped feature to apply dynamic status qualifiers for token-gating experiences. Enables organizations to verify that principal token owners meet all requirements for community participation through flexible support for subscriptions, Terms of Service compliance, VIP experiences, and decentralized user management. Status changes can be triggered by contract owners, designated managers, token owners, or programmable functions responding to web2 or on-chain interactions. Provides robust eligibility verification with the ability to disable token-gated access for non-compliant behavior while preserving original asset ownership and blockchain immutability.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Number
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    U.S. Patent No. 12,293,358
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Patent Title
                  </Text>
                  <Text>Mitigation of Cryptographic Asset Attacks</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
                    Issue Date
                  </Text>
                  <Text>May 6, 2025</Text>
                </Box>

                <Box>
                  <ChakraLink
                    href="https://patentsgazette.uspto.gov/week18/OG/html/1534-1/US12293358-20250506.html"
                    isExternal
                    color={useColorModeValue('brand.700', 'brand.300')}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    View Patent at USPTO <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        <Divider my="8" />

        <Box textAlign="center" py={8}>
          <Text fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')} mb={4}>
            This virtual patent marking page complies with the provisions of 35 U.S.C. § 287(a),
            which allows patent holders to provide constructive notice of patent rights through an
            internet address.
          </Text>
          <Text fontSize="sm" color={useColorModeValue('slate.600', 'slate.400')}>
            For licensing inquiries, please contact:{' '}
            <ChakraLink
              href="mailto:sales@foolproof.xyz"
              color={useColorModeValue('brand.700', 'brand.300')}
              _hover={{ textDecoration: 'underline' }}
            >
              sales@foolproof.xyz
            </ChakraLink>
          </Text>
        </Box>
      </Container>
    </Box>
  )
}

export default Patents
